import { useReducer, useState, useCallback, useEffect, useRef } from 'react'
import { logger } from '@/lib/logger';
import { SettingsState, SettingsAction, DEFAULT_SETTINGS } from '@/types/settings'
import { settingsReducer } from '@/lib/settings-reducer'

export const useSettings = () => {
  const [settings, dispatch] = useReducer(settingsReducer, DEFAULT_SETTINGS)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>()

  // Load saved settings on mount
  useEffect(() => {
    const saved = localStorage.getItem('sws-settings')
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved)
        dispatch({ type: 'IMPORT_SETTINGS', settings: { ...DEFAULT_SETTINGS, ...parsedSettings } })
      } catch (error) {
        logger.warn('Failed to load saved settings:', error)
      }
    }
  }, [])

  // Auto-save functionality
  useEffect(() => {
    if (!settings.autoSave || !hasUnsavedChanges) return

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem('sws-settings', JSON.stringify(settings))
      setHasUnsavedChanges(false)
    }, 1000) // 1 second auto-save

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [settings, hasUnsavedChanges])

  const handleSettingChange = useCallback((key: keyof SettingsState, value: any) => {
    // Validate and clamp numeric values to their proper ranges
    let validatedValue = value
    if (typeof value === 'number') {
      switch (key) {
        case 'particleCount':
          validatedValue = Math.max(100, Math.min(10000, Math.round(value / 100) * 100))
          break
        case 'frameRate':
          validatedValue = Math.max(30, Math.min(120, Math.round(value / 10) * 10))
          break
        case 'memoryLimit':
          validatedValue = Math.max(128, Math.min(2048, Math.round(value / 128) * 128))
          break
        case 'maxWorkers':
          validatedValue = Math.max(1, Math.min(16, Math.round(value)))
          break
        default:
          validatedValue = value
      }
    }

    dispatch({ type: 'UPDATE_SETTING', key, value: validatedValue })
    setHasUnsavedChanges(true)
  }, [])

  const handleResetSection = useCallback((section: string) => {
    dispatch({ type: 'RESET_SECTION', section })
    setHasUnsavedChanges(true)
  }, [])

  const handleResetAll = useCallback(() => {
    dispatch({ type: 'RESET_ALL' })
    setHasUnsavedChanges(true)
  }, [])

  const handleExport = useCallback(() => {
    dispatch({ type: 'EXPORT_SETTINGS' })
  }, [])

  const handleLoadPreset = useCallback((preset: Partial<SettingsState>) => {
    dispatch({ type: 'LOAD_PRESET', preset })
    setHasUnsavedChanges(true)
  }, [])

  const handleImport = useCallback((settings: SettingsState) => {
    dispatch({ type: 'IMPORT_SETTINGS', settings })
    setHasUnsavedChanges(true)
  }, [])

  return {
    settings,
    hasUnsavedChanges,
    handleSettingChange,
    handleResetSection,
    handleResetAll,
    handleExport,
    handleLoadPreset,
    handleImport
  }
}